// src/client/widget-loader.js
var widgetConfigLib = require('./lib/widget-config');

(function() {
    // ------------------------------------------------------------------------
    // Config API (Phase 1) — shared with unit tests via ./lib/widget-config.js
    // ------------------------------------------------------------------------
    var mergeConfig = widgetConfigLib.mergeConfig;

    // ------------------------------------------------------------------------
    // Instance registry & fetch/script memoization (Phase 2)
    // ------------------------------------------------------------------------
    window.__eeReservationWidgets = window.__eeReservationWidgets || {};
    // Separate caches for text and JSON so a URL used by both helpers never
    // silently returns the wrong type (addresses Cursor Bugbot finding).
    window.__eeReservationWidgetTextCache = window.__eeReservationWidgetTextCache || {};
    window.__eeReservationWidgetJsonCache = window.__eeReservationWidgetJsonCache || {};
    window.__eeReservationWidgetScriptsLoaded = window.__eeReservationWidgetScriptsLoaded || {};
    // Per-instance pending-init slots. Keyed by instanceId so concurrent
    // loadReservationWidget() calls do not race on a single global.
    window.__eeReservationWidgetPendingInits = window.__eeReservationWidgetPendingInits || {};
    // Per-instance stale-data cleanup functions. Each client-script init pass
    // pushes its own closure here; window.cleanupStaleDataCheck iterates and
    // calls them all. Prevents leaked setInterval timers in multi-instance
    // setups where the previous single-global was clobbered per instance.
    window.__eeReservationWidgetCleanups = window.__eeReservationWidgetCleanups || [];

    function generateInstanceId() {
        var rand = (window.crypto && window.crypto.randomUUID)
            ? window.crypto.randomUUID().replace(/-/g, '').slice(0, 8)
            : Math.random().toString(36).slice(2, 10);
        return 'r' + rand;
    }

    function loadScript(src) {
        if (window.__eeReservationWidgetScriptsLoaded[src]) {
            return window.__eeReservationWidgetScriptsLoaded[src];
        }
        var p = new Promise(function(resolve, reject) {
            var script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.onload = resolve;
            script.onerror = function() {
                reject(new Error('Failed to load script: ' + src));
            };
            document.head.appendChild(script);
        }).catch(function(err) {
            // Remove the failed entry so a retry can fetch fresh.
            delete window.__eeReservationWidgetScriptsLoaded[src];
            throw err;
        });
        window.__eeReservationWidgetScriptsLoaded[src] = p;
        return p;
    }

    function cachedFetchText(url) {
        if (window.__eeReservationWidgetTextCache[url]) {
            return window.__eeReservationWidgetTextCache[url];
        }
        var p = fetch(url).then(function(r) {
            // Treat non-OK responses as failures. Without this check, a 500
            // response body (HTML error page) would be cached as successful
            // content and injected into the DOM on the next retry.
            if (!r.ok) {
                throw new Error('Request failed: ' + r.status);
            }
            return r.text();
        }).catch(function(err) {
            // Don't poison the cache with a rejected promise — a transient
            // network failure or server error should not prevent a subsequent
            // load from succeeding. Clear the slot so the next call re-fetches.
            delete window.__eeReservationWidgetTextCache[url];
            throw err;
        });
        window.__eeReservationWidgetTextCache[url] = p;
        return p;
    }

    function cachedFetchJson(url) {
        if (window.__eeReservationWidgetJsonCache[url]) {
            return window.__eeReservationWidgetJsonCache[url];
        }
        var p = fetch(url).then(function(response) {
            if (!response.ok) {
                throw new Error('Request failed: ' + response.status);
            }
            return response.json();
        }).catch(function(err) {
            delete window.__eeReservationWidgetJsonCache[url];
            throw err;
        });
        window.__eeReservationWidgetJsonCache[url] = p;
        return p;
    }

    // ------------------------------------------------------------------------
    // ID scoping helpers (Phase 3)
    //
    // Wraps the server-rendered fragment in a per-instance class so CSS can
    // scope to a single widget, and exposes helpers for future per-instance
    // DOM lookups. The current single-instance-per-page implementation keeps
    // the legacy fixed IDs; wrapper + helpers are infrastructure for a future
    // multi-instance sweep of client-script.js.
    // ------------------------------------------------------------------------
    function wrapFragmentForInstance(fragmentRoot, instanceId, mode) {
        var wrapper = document.createElement('div');
        wrapper.className = 'ee-res-widget-root ee-res-mode-' + mode + ' ee-res-' + instanceId;
        wrapper.setAttribute('data-ee-instance-id', instanceId);
        while (fragmentRoot.firstChild) {
            wrapper.appendChild(fragmentRoot.firstChild);
        }
        return wrapper;
    }

    /**
     * Main function to load the reservation widget.
     * @param {string} venueId - The venue's CorporationDetailId (GUID)
     * @param {Object} [userConfig] - Optional configuration (defaults in ./lib/widget-config.js)
     * @param {'modal'|'embedded'} [userConfig.mode='modal'] - Invalid values warn and fall back to modal
     * @param {string} [userConfig.targetElementId='ee-reservation-widget'] - Embedded only: host container id
     * @param {string|null} [userConfig.maxHeight=null] - Embedded only: CSS max-height (e.g. '720px'); internal scroll when set
     *
     * Modal mode: triggers are elements with class `spotliteModalTriggerButton` (not a config option).
     */
    function loadWidget(venueId, userConfig) {
        var config = mergeConfig(userConfig);
        var instanceId = generateInstanceId();

        // Enforce same-venueId-only multi-instance contract.
        var existingIds = Object.keys(window.__eeReservationWidgets);
        if (existingIds.length > 0) {
            var firstVenue = window.__eeReservationWidgets[existingIds[0]].venueId;
            if (firstVenue !== venueId) {
                console.error('[EE Reservation Widget] Multiple instances must share the same venueId. Existing: ' + firstVenue + ', requested: ' + venueId + '. Aborting new instance.');
                return;
            }
        }

        // Embedded mode requires a target element in the DOM.
        var targetElement = null;
        if (config.mode === 'embedded') {
            targetElement = document.getElementById(config.targetElementId);
            if (!targetElement) {
                console.error('[EE Reservation Widget] Embedded target element #' + config.targetElementId + ' not found. Add <div id="' + config.targetElementId + '"></div> to your page before calling loadReservationWidget.');
                return;
            }
        }

        // Register the instance before any async work so in-flight events can find it.
        window.__eeReservationWidgets[instanceId] = {
            instanceId: instanceId,
            venueId: venueId,
            config: config,
            container: null,
            mode: config.mode,
            targetElement: targetElement,
            data: null
        };

        // Clean up any existing stale data checks from previous widget instances
        // (only relevant for the single-instance legacy path).
        if (existingIds.length === 0 && window.cleanupStaleDataCheck) {
            window.cleanupStaleDataCheck();
        }

        // Use the base URL injected by Webpack; defaults to localhost if not overridden.
        const baseUrl = EAGLEEYE_WIDGET_BASE_URL;

        // Public tracking + third-party pixels (runs on host page)
        const TRACKING_SURFACE = 'widget_reservation_submission';
        const ensureScriptOnce = (id, src) => new Promise((resolve) => {
            try {
                if (document.getElementById(id)) return resolve();
                const s = document.createElement('script');
                s.id = id;
                s.async = true;
                s.src = src;
                s.onload = () => resolve();
                s.onerror = () => resolve();
                document.head.appendChild(s);
            } catch (_) { resolve(); }
        });

        const getOrCreateId = (storage, key) => {
            try {
                const existing = storage.getItem(key);
                if (existing) return existing;
                const id = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : ('ee_' + Math.random().toString(36).slice(2) + Date.now().toString(36));
                storage.setItem(key, id);
                return id;
            } catch (_) {
                return ('ee_' + Math.random().toString(36).slice(2) + Date.now().toString(36));
            }
        };

        const safeSessionStorageGet = (key) => {
            try { return window.sessionStorage.getItem(key); } catch (_) { return null; }
        };
        const safeSessionStorageSet = (key, value) => {
            try { window.sessionStorage.setItem(key, value); } catch (_) {}
        };

        const getOrCreateSessionId = (tracker) => {
            const policy = tracker?._eagleEyeSessionPolicy || 'TabLifetime';
            const ttlMinutesRaw = tracker?._eagleEyeSessionTtlMinutes;
            const ttlMinutes = (ttlMinutesRaw == null) ? null : Number(ttlMinutesRaw);
            const now = Date.now();
            const sessionIdKey = 'ee_session_id';
            const sessionMetaKey = 'ee_session_meta';

            if (policy === 'PageLoad') {
                if (!tracker._pageLoadSessionId) {
                    tracker._pageLoadSessionId = (window.crypto && window.crypto.randomUUID)
                        ? window.crypto.randomUUID()
                        : ('ee_' + Math.random().toString(36).slice(2) + Date.now().toString(36));
                }
                safeSessionStorageSet(sessionIdKey, tracker._pageLoadSessionId);
                return tracker._pageLoadSessionId;
            }

            if (policy === 'TabLifetime') {
                const existing = safeSessionStorageGet(sessionIdKey);
                if (existing) return existing;
                const id = getOrCreateId(window.sessionStorage, sessionIdKey);
                return id;
            }

            const ttlMs = (ttlMinutes != null && !Number.isNaN(ttlMinutes)) ? Math.max(1, ttlMinutes) * 60 * 1000 : null;
            let meta = null;
            try {
                const raw = safeSessionStorageGet(sessionMetaKey);
                meta = raw ? JSON.parse(raw) : null;
            } catch (_) { meta = null; }

            const currentId = (meta && typeof meta.id === 'string') ? meta.id : null;
            const createdUtcMs = (meta && typeof meta.createdUtcMs === 'number') ? meta.createdUtcMs : null;
            const lastSeenUtcMs = (meta && typeof meta.lastSeenUtcMs === 'number') ? meta.lastSeenUtcMs : null;

            const shouldRotate = ttlMs != null && (
                (policy === 'IdleTimeoutMinutes' && lastSeenUtcMs != null && (now - lastSeenUtcMs) > ttlMs) ||
                (policy === 'AbsoluteTimeoutMinutes' && createdUtcMs != null && (now - createdUtcMs) > ttlMs)
            );

            const id = (!currentId || shouldRotate) ? ((window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : ('ee_' + Math.random().toString(36).slice(2) + Date.now().toString(36))) : currentId;
            const nextMeta = {
                id,
                createdUtcMs: (!currentId || shouldRotate) ? now : (createdUtcMs || now),
                lastSeenUtcMs: now
            };
            try { safeSessionStorageSet(sessionMetaKey, JSON.stringify(nextMeta)); } catch (_) {}
            return id;
        };

        const resolvePrimarySource = (tracker, payload) => {
            const pSourceId = payload?.sourceId ?? null;
            const pSourceIdType = payload?.sourceIdType ?? null;
            if (pSourceId) {
                return { sourceId: pSourceId, sourceIdType: pSourceIdType || 'Other' };
            }

            const ctx = tracker?._context || {};
            if (ctx.reservationId) return { sourceId: ctx.reservationId, sourceIdType: 'ReservationId' };
            if (ctx.pendingReservationId) return { sourceId: ctx.pendingReservationId, sourceIdType: 'PendingReservationId' };
            if (ctx.experiencePurchaseId) return { sourceId: ctx.experiencePurchaseId, sourceIdType: 'ExperiencePurchaseId' };
            if (ctx.paymentIntentId) return { sourceId: ctx.paymentIntentId, sourceIdType: 'PaymentIntentId' };
            return { sourceId: tracker?._venueId || null, sourceIdType: 'VenueId' };
        };

        const resolveMapping = (mappings, provider, internalEvent) => {
            const list = mappings || [];
            for (let i = 0; i < list.length; i++) {
                const m = list[i];
                if ((m.provider || '') === provider && (m.internalEvent || '').trim() === (internalEvent || '').trim()) return m;
            }
            return null;
        };

        const defaultMetaEvent = (internalEvent) => {
            const e = (internalEvent || '').toLowerCase();
            if (e === 'page_view') return 'PageView';
            if (e === 'begin_checkout') return 'InitiateCheckout';
            if (e === 'add_payment_info') return 'AddPaymentInfo';
            if (e === 'reservation_confirmed' || e === 'purchase') return 'Purchase';
            return 'CustomEvent';
        };

        const defaultTikTokEvent = (internalEvent) => {
            const e = (internalEvent || '').toLowerCase();
            if (e === 'page_view') return 'PageView';
            if (e === 'begin_checkout') return 'InitiateCheckout';
            if (e === 'add_payment_info') return 'AddPaymentInfo';
            if (e === 'reservation_confirmed' || e === 'purchase') return 'CompletePayment';
            return 'Custom';
        };

        const ensureMetaPixelBootstrap = () => {
            if (window.fbq) return;
            (function(f,b,e,v,n,t,s){
                if (f.fbq) return;
                n = f.fbq = function(){ n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
                if (!f._fbq) f._fbq = n;
                n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
                t = b.createElement(e); t.async = true; t.src = v;
                s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
            })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
        };

        const ensureTikTokPixelBootstrap = () => {
            if (window.ttq) return;
            (function (w, d, t) {
                w.TiktokAnalyticsObject = t;
                const ttq = w[t] = w[t] || [];
                ttq.methods = ['page','track','identify','instances','debug','on','off','once','ready','alias','group','enableCookie','disableCookie'];
                ttq.setAndDefer = function(target, method) { target[method] = function() { target.push([method].concat([].slice.call(arguments, 0))); }; };
                for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
                ttq.instance = function(id) {
                    const inst = ttq._i[id] = [];
                    for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(inst, ttq.methods[i]);
                    return inst;
                };
                ttq.load = function(id) {
                    const s = d.createElement('script');
                    s.type = 'text/javascript'; s.async = true;
                    s.src = 'https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=' + id + '&lib=' + t;
                    const x = d.getElementsByTagName('script')[0];
                    x.parentNode.insertBefore(s, x);
                };
                ttq._i = {}; ttq._o = {}; ttq._partner = '';
            })(window, document, 'ttq');
        };

        const ensureGtagBootstrap = () => {
            window.dataLayer = window.dataLayer || [];
            if (typeof window.gtag === 'function') return;
            window.gtag = function() { window.dataLayer.push(arguments); };
            window.gtag('js', new Date());
        };

        const initReservationTracking = () => {
            if (window.eeReservationWidgetTracking && window.eeReservationWidgetTracking._venueId === venueId) return window.eeReservationWidgetTracking._readyPromise || Promise.resolve();
            const tracker = window.eeReservationWidgetTracking || {};
            tracker._venueId = venueId;
            tracker._baseUrl = baseUrl;
            tracker._providers = [];
            tracker._mappings = [];
            tracker._eagleEyeEnabled = false;
            tracker._readyPromise = fetch(`${baseUrl}/pixel-settings?venueId=${encodeURIComponent(venueId)}`)
                .then(r => r.json())
                .then(cfg => {
                    tracker._providers = cfg?.providers || [];
                    tracker._mappings = cfg?.eventMappings || [];
                    tracker._eagleEyeEnabled = !!cfg?.eagleEyeEnabled;
                    tracker._eagleEyeSessionPolicy = cfg?.eagleEyeSessionPolicy || 'TabLifetime';
                    tracker._eagleEyeSessionTtlMinutes = cfg?.eagleEyeSessionTtlMinutes ?? null;
                    tracker._context = tracker._context || { venueId: venueId };
                    tracker.setContext = (partial) => {
                        try {
                            tracker._context = tracker._context || { venueId: venueId };
                            Object.assign(tracker._context, partial || {});
                        } catch (_) {}
                    };

                    // gtag (GA4/GoogleAds)
                    const ga4 = tracker._providers.find(p => p.provider === 'GA4' && p.clientId);
                    const ads = tracker._providers.find(p => p.provider === 'GoogleAds' && p.clientId);
                    const firstId = (ga4?.clientId || ads?.clientId);
                    if (firstId) {
                        return ensureScriptOnce('ee-res-gtag', `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(firstId)}`)
                            .then(() => {
                                ensureGtagBootstrap();
                                if (ga4?.clientId) window.gtag('config', ga4.clientId);
                                if (ads?.clientId) window.gtag('config', ads.clientId);
                            });
                    }
                })
                .catch(() => {});

            tracker.track = (eventName, payload) => {
                try {
                    const skipThirdParty = !!(payload && payload.skipThirdParty === true);
                    const visitorId = getOrCreateId(window.localStorage, 'ee_visitor_id');
                    const sessionId = getOrCreateSessionId(tracker);
                    const eventId = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : ('ee_' + Math.random().toString(36).slice(2));
                    const primary = resolvePrimarySource(tracker, payload);
                    const body = {
                        corporationDetailId: venueId,
                        eventName,
                        surface: TRACKING_SURFACE,
                        visitorId,
                        sessionId,
                        eventId,
                        url: window.location.href,
                        referrer: document.referrer || null,
                        // Sent from the browser so the visitor's device survives the widget
                        // server hop, which strips it otherwise (EAG-1202).
                        userAgent: (navigator && navigator.userAgent) || null,
                        value: payload?.valueCents ?? null,
                        currency: payload?.currency ?? null,
                        sourceId: primary.sourceId,
                        sourceIdType: primary.sourceIdType,
                        secondarySourceId: venueId,
                        secondarySourceIdType: 'VenueId',
                        email: payload?.email ?? null,
                        phoneNumber: payload?.phoneNumber ?? null,
                        properties: payload?.properties ?? null
                    };

                    if (tracker._eagleEyeEnabled === true) {
                        fetch(`${baseUrl}/tracking/event`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'X-Widget-Origin': window.location.origin },
                            body: JSON.stringify(body),
                            keepalive: true
                        }).catch(() => {});
                    }

                    if (skipThirdParty) {
                        return;
                    }

                    // Meta
                    const meta = tracker._providers.find(p => p.provider === 'Meta' && p.clientId);
                    if (meta?.clientId) {
                        ensureMetaPixelBootstrap();
                        window.fbq('init', meta.clientId);
                        const m = resolveMapping(tracker._mappings, 'Meta', eventName);
                        const metaEvent = (m?.providerEventName || '').trim() || defaultMetaEvent(eventName);
                        const params = {};
                        if (payload?.valueCents != null) params.value = payload.valueCents / 100;
                        if (payload?.currency) params.currency = payload.currency;
                        window.fbq('track', metaEvent, params, { eventID: eventId });
                    }

                    // TikTok
                    const tk = tracker._providers.find(p => p.provider === 'TikTok' && p.clientId);
                    if (tk?.clientId) {
                        ensureTikTokPixelBootstrap();
                        window.ttq.load(tk.clientId);
                        const t = resolveMapping(tracker._mappings, 'TikTok', eventName);
                        const tkEvent = (t?.providerEventName || '').trim() || defaultTikTokEvent(eventName);
                        const tkParams = {};
                        if (payload?.valueCents != null) tkParams.value = payload.valueCents / 100;
                        if (payload?.currency) tkParams.currency = payload.currency;
                        window.ttq.track(tkEvent, tkParams);
                    }

                    // Google (GA4 + Google Ads)
                    const ga4p = tracker._providers.find(p => p.provider === 'GA4' && p.clientId);
                    const adsp = tracker._providers.find(p => p.provider === 'GoogleAds' && p.clientId);
                    if (ga4p?.clientId || adsp?.clientId) {
                        ensureGtagBootstrap();
                        const gParams = {};
                        if (payload?.valueCents != null) gParams.value = payload.valueCents / 100;
                        if (payload?.currency) gParams.currency = payload.currency;

                        if (ga4p?.clientId) {
                            const g = resolveMapping(tracker._mappings, 'GA4', eventName);
                            const ga4Event = (g?.providerEventName || '').trim() || eventName;
                            window.gtag('event', ga4Event, Object.assign({ send_to: ga4p.clientId }, gParams));
                        }

                        if (adsp?.clientId) {
                            const a = resolveMapping(tracker._mappings, 'GoogleAds', eventName);
                            const label = (a?.providerConversionId || '').trim();
                            if (label) {
                                const sendTo = label.indexOf('/') >= 0 ? label : (adsp.clientId + '/' + label);
                                const txnId = (primary?.sourceId && primary.sourceIdType !== 'VenueId') ? primary.sourceId : eventId;
                                window.gtag('event', 'conversion', Object.assign({ send_to: sendTo, transaction_id: txnId }, gParams));
                            }
                        }
                    }
                } catch (_) {}
            };

            // Out-of-the-box (OOB) analytics helpers (EagleEye-only by default)
            tracker._oob = tracker._oob || {
                widget: 'reservation_submission',
                mode: 'modal',
                widgetLoadMs: Date.now(),
                stepViewMs: Date.now(),
                step: null,
                stepIndex: null,
                formStarted: false,
                once: {}
            };

            tracker._oobTrack = (eventName, props) => {
                try {
                    const now = Date.now();
                    const baseProps = {
                        widget: tracker._oob.widget,
                        mode: tracker._oob.mode,
                        step: tracker._oob.step,
                        stepIndex: tracker._oob.stepIndex,
                        timeSinceWidgetLoadMs: now - tracker._oob.widgetLoadMs,
                        timeSinceStepViewMs: now - tracker._oob.stepViewMs
                    };
                    // Merge order: baseProps first, caller props second — so a
                    // call-site can stamp a per-instance `mode` that overrides
                    // the shared tracker default (prevents the last-loader-wins
                    // problem flagged by Cursor Bugbot).
                    tracker.track(eventName, { skipThirdParty: true, properties: Object.assign({}, baseProps, props || {}) });
                } catch (_) {}
            };

            tracker._oobImpressionOnce = (componentKey) => {
                try {
                    const key = `impression:${tracker._oob.widget}:${tracker._oob.mode}:${tracker._oob.step || 'unknown'}:${componentKey}`;
                    if (tracker._oob.once[key]) return;
                    tracker._oob.once[key] = true;
                    tracker._oobTrack('impression', { componentKey });
                } catch (_) {}
            };

            tracker._oobSetStep = (step, stepIndex) => {
                try {
                    const changed = tracker._oob.step !== step || tracker._oob.stepIndex !== stepIndex;
                    tracker._oob.step = step;
                    tracker._oob.stepIndex = stepIndex;
                    tracker._oob.stepViewMs = Date.now();
                    if (changed) {
                        tracker._oobTrack('screen_view', {});
                        tracker._oobTrack('form_step_view', {});
                    }
                } catch (_) {}
            };

            tracker._oobFormStart = () => {
                try {
                    if (tracker._oob.formStarted) return;
                    tracker._oob.formStarted = true;
                    tracker._oobTrack('form_start', {});
                } catch (_) {}
            };

            tracker._oobValidationError = (field, rule) => tracker._oobTrack('validation_error', { field, rule });
            tracker._oobApiError = (endpointGroup, statusCode) => tracker._oobTrack('api_error', { endpointGroup, statusCode });
            tracker._oobPaymentError = (provider, errorCode) => tracker._oobTrack('payment_error', { provider, errorCode });
            tracker._oobAbandon = (reason) => tracker._oobTrack('abandon', { reason });

            window.eeReservationWidgetTracking = tracker;
            return tracker._readyPromise || Promise.resolve();
        };
        
        // Check for skipCache flag in widget config (used in preview mode)
        const skipCache = window.reservationWidgetConfig?.skipCache === true;
        const cacheParam = skipCache ? '&skipCache=true' : '';
        
        try {
            initReservationTracking().then(() => {
                if (window.eeReservationWidgetTracking && typeof window.eeReservationWidgetTracking.track === 'function') {
                    try {
                        // Do NOT write config.mode onto the shared tracker's
                        // _oob.mode — with multiple instances on the page the
                        // last load would clobber earlier ones. Pass mode as a
                        // per-call property instead; _oobTrack's merge order
                        // lets per-call props override the shared default.
                        window.eeReservationWidgetTracking._oobTrack?.('widget_loaded', { venueId: venueId, mode: config.mode });
                    } catch (_) { /* ignore */ }
                    window.eeReservationWidgetTracking.track('page_view', { sourceId: venueId, properties: { widget: 'reservation_submission', mode: config.mode } });
                }
            });
        } catch (_) { /* ignore */ }

        // Step 1: Fetch bootstrap data to determine payment provider (cached per venueId)
        var bootstrapUrl = baseUrl + '/api/widget/bootstrap?venueId=' + encodeURIComponent(venueId);
        cachedFetchJson(bootstrapUrl)
            .then((bootstrapData) => {
                // Store bootstrap data for use by the widget
                window.widgetBootstrap = bootstrapData;
                const paymentProvider = bootstrapData?.paymentProvider?.toUpperCase() || 'STRIPE';

                // Step 2: Fetch the widget HTML content and data from the server (cached per venueId).
                var widgetUrl = baseUrl + '/widget?venueId=' + encodeURIComponent(venueId) + cacheParam;
                return cachedFetchText(widgetUrl)
                    .then((htmlContent) => {
                        // Create a container to parse the HTML content
                        const container = document.createElement('div');
                        container.innerHTML = htmlContent;

                        // Extract and execute scripts
                        const scripts = container.querySelectorAll('script');
                        scripts.forEach((script) => {
                            if (script.src) {
                                // External script (idempotent via loadScript cache)
                                loadScript(script.src).catch(function(e) {
                                    console.error('Failed to load external widget script:', script.src, e);
                                });
                            } else {
                                // Inline script — run once. For a second instance with the same venueId
                                // the script body (`window.widgetData = {...}`) has already populated
                                // the global; running it again is harmless and idempotent.
                                try {
                                    eval(script.textContent);
                                } catch (e) {
                                    console.error('Error executing script:', e);
                                }
                            }
                            // Remove script from container
                            script.parentNode.removeChild(script);
                        });

                        // Wrap the fragment in a per-instance scoped container so CSS overrides
                        // (embedded-mode styling) apply only to this widget.
                        var scoped = wrapFragmentForInstance(container, instanceId, config.mode);

                        // Mount point depends on mode:
                        //   modal    -> document.body (fixed-position overlay)
                        //   embedded -> user-provided target element
                        var mountTarget;
                        if (config.mode === 'embedded') {
                            // Clear any placeholder content inside the target (safe: we own this element now).
                            while (targetElement.firstChild) targetElement.removeChild(targetElement.firstChild);
                            mountTarget = targetElement;
                            // Mirror data-spot-* hints from target element so the client script
                            // can read them during init (embedded has no trigger button).
                            var spotAttrs = ['data-spot-booking-area-id', 'data-spot-party-min', 'data-spot-party-max'];
                            spotAttrs.forEach(function(attr) {
                                var v = targetElement.getAttribute(attr);
                                if (v !== null) scoped.setAttribute(attr, v);
                            });
                        } else {
                            mountTarget = document.body;
                        }
                        mountTarget.appendChild(scoped);

                        // Record the instance's container reference so client-script can find it.
                        window.__eeReservationWidgets[instanceId].container = scoped;
                        window.__eeReservationWidgets[instanceId].data = window.widgetData || null;

                        // Expose the current instance id + config to client-script for this init pass.
                        // Keyed by instanceId so concurrent loads don't overwrite each other's
                        // pending config (race fix from review #2602).
                        var pendingConfig = {
                            instanceId: instanceId,
                            mode: config.mode,
                            maxHeight: config.maxHeight,
                            container: scoped,
                            targetElement: targetElement
                        };
                        window.__eeReservationWidgetPendingInits[instanceId] = pendingConfig;
                        // Legacy single-slot mirror for any external code still reading the
                        // pre-2602 global. Safe to overwrite — the per-instance map above is
                        // authoritative.
                        window.__eeReservationWidgetPendingInit = pendingConfig;

                        // Step 3: Load dependencies in order (each script is fetch-once via loadScript cache).
                        return loadScript('https://cdn.jsdelivr.net/npm/luxon@3.0.0/build/global/luxon.min.js')
                            .then(() => {
                                return loadScript(`${baseUrl}/public/js/widget-core.bundle.js`);
                            })
                            .then(() => {
                                const paymentBundleUrl = (paymentProvider === 'ADYEN')
                                    ? `${baseUrl}/public/js/payment-adyen.bundle.js`
                                    : `${baseUrl}/public/js/payment-stripe.bundle.js`;
                                return loadScript(paymentBundleUrl);
                            })
                            .then(() => {
                                // Initialize the widget after all scripts are loaded.
                                // Pass the instanceId so init reads the correct pending-slot
                                // even when multiple loads are in flight concurrently.
                                if (window.initializeReservationWidget) {
                                    window.initializeReservationWidget(instanceId);
                                }

                                // Dispatch per-instance ready event.
                                const widgetReadyEvent = new CustomEvent('ee-widget-ready', {
                                    detail: {
                                        paymentProvider: paymentProvider,
                                        venueId: venueId,
                                        mode: config.mode,
                                        instanceId: instanceId,
                                        widgetData: window.widgetData || null
                                    }
                                });
                                document.dispatchEvent(widgetReadyEvent);
                            });
                    });
            })
            .catch((error) => {
                console.error('Error loading widget:', error);
                // Clear the registry slot on fatal load failure so retries are possible.
                try { delete window.__eeReservationWidgets[instanceId]; } catch (_) { /* ignore */ }
            });

        // Legacy single-instance guard kept for any external scripts that inspect it.
        window.eagleEyeWidgetInitialized = true;
    }

    // Expose the loadWidget function globally (signature: venueId, optional config)
    window.loadReservationWidget = loadWidget;
})();
